import React, {useEffect, useRef, useState} from 'react';
import {FiArrowRight, FiCheck, FiCompass, FiEye, FiHelpCircle, FiRotateCcw} from 'react-icons/fi';
import {MotionMobileActions} from './MotionReviewHelp';
import CitizenScienceTutorialClip from './CitizenScienceTutorialClip';
import {trackCommunity} from './analytics';
import './citizenScienceTutorial.css';

const labels = {animal: 'Real motion', false_alarm: 'False alarm', predator: 'Predator', interesting: 'Interesting', unsure: 'Not sure'};

export default function CitizenScienceTutorial({api, children, autoStart = false, onExit, active = true}) {
  const [pack, setPack] = useState(null);
  const [started, setStarted] = useState(autoStart);
  const [celebrate, setCelebrate] = useState(false);
  const [bypass, setBypass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const alive = useRef(false);
  const savingLock = useRef(false);
  const resumed = useRef(false);
  const trackedStart = useRef(false);
  const pendingLoadTracking = useRef(null);
  const heading = useRef(null);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);
  useEffect(() => {
    let active = true;
    setLoading(true); setError('');
    api.load().then(value => {
      if (!active) return;
      if (!value || (!value.completed && !value.skipped && (!Array.isArray(value.lessons) || value.lessons.length !== value.total || !value.lessons[value.completedSteps]))) {
        throw new Error('Practice is temporarily unavailable.');
      }
      setPack(value); setSelected(null); setReady(false); setShowHint(false);
      pendingLoadTracking.current = value;
      if (!value.completed && value.completedSteps > 0) {
        setStarted(true);
      }
    }).catch(() => { if (active) setError('We couldn’t load your practice. Check your connection, then try again.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [api, reload, autoStart]);
  useEffect(() => {
    const value = pendingLoadTracking.current;
    if (!active || !value) return;
    pendingLoadTracking.current = null;
    if (value.completed || value.skipped) return;
    if (autoStart && !trackedStart.current && value.completedSteps === 0) {
      trackedStart.current = true;
      trackCommunity('cs_tutorial_started', {tutorial_version: value.version, total_steps: value.total, test_mode: Boolean(value.preview)});
    }
    if (value.completedSteps > 0 && !resumed.current) {
      resumed.current = true;
      trackCommunity('cs_tutorial_resumed', {tutorial_version: value.version, step: value.completedSteps + 1});
    }
  }, [active, autoStart, pack]);
  useEffect(() => {
    if (active && started && !loading) heading.current?.focus();
  }, [active, started, loading, pack?.completedSteps]);

  const retry = () => setReload(value => value + 1);
  const restart = () => { setBypass(false); setStarted(false); setCelebrate(false); setSelected(null); retry(); };
  if (loading || !pack) return <div className="community-app cs-tutorial"><main className="cs-tutorial-message">
    <FiCompass className="cs-tutorial-mark" aria-hidden="true" />
    {error ? <><h1>Let’s reconnect</h1><p role="alert">{error}</p><button className="cr-primary" onClick={retry}>Try again</button></> : <p role="status">Preparing your practice…</p>}
  </main></div>;
  if (bypass || pack.skipped || (pack.completed && !celebrate)) return pack.preview && !onExit ? <>
    <div className="cs-tutorial-test-bar"><span>Introduction · test mode</span><button type="button" onClick={restart}>Replay introduction</button></div>
    {children}
  </> : children;
  if (error && !selected) return <div className="community-app cs-tutorial"><main className="cs-tutorial-message"><h1>Let’s reconnect</h1><p role="alert">{error}</p><button className="cr-primary" onClick={retry}>Try again</button></main></div>;

  const start = () => {
    if (!active) return;
    trackCommunity('cs_tutorial_started', {tutorial_version: pack.version, total_steps: pack.total, test_mode: Boolean(pack.preview)});
    setStarted(true);
  };
  if (!started) return <div className="community-app cs-tutorial"><main className="cs-tutorial-welcome">
    <div className="cs-tutorial-viewfinder" aria-hidden="true"><FiEye /><span>LOOK CLOSER</span></div>
    <h1>Train your<br /><em>wildlife eye.</em></h1>
    <p>Ten real clips. A few easy spots, some false alarms, and two hidden animals. We’ll guide you through each one.</p>
    <ul className="cs-tutorial-intro-list">
      <li><FiEye aria-hidden="true" /><span><strong>Watch the whole picture</strong>Boxes are clues, not proof of an animal.</span></li>
      <li><FiHelpCircle aria-hidden="true" /><span><strong>Learn by tapping</strong>Get a tip after every answer. No pressure.</span></li>
      <li><FiCheck aria-hidden="true" /><span><strong>{pack.preview ? 'Replay as often as you like' : 'One introduction, then you’re off'}</strong>{pack.preview ? 'Test mode is on. Once-only is switched off.' : 'We’ll save your place to your account.'}</span></li>
    </ul>
    <button className="cr-primary cs-tutorial-start" onClick={start}>Start practice <FiArrowRight aria-hidden="true" /></button>
    {pack.preview && <button className="cs-tutorial-undo" onClick={() => setBypass(true)}>Skip to game</button>}
    <p className="cs-tutorial-footnote">Practice answers don’t affect your reviews or score.</p>
  </main></div>;

  if (celebrate) return <div className="community-app cs-tutorial"><main className="cs-tutorial-message cs-tutorial-finished">
    <span className="cs-tutorial-finished-mark"><FiCheck aria-hidden="true" /></span>
    <span className="cs-tutorial-kicker">{pack.total} OF {pack.total} · PRACTICE COMPLETE</span>
    <h1 ref={heading} tabIndex={-1}>You’re ready<br />to spot wildlife.</h1>
    <p>Watch the whole picture. Flag predators and interesting sightings. And when you can’t tell, choose Not sure.</p>
    <p className="cs-tutorial-saved"><FiCheck aria-hidden="true" />{pack.preview ? 'Practice complete. Your saved progress stays the same.' : 'Saved to your account. This introduction won’t appear again.'}</p>
    <button className="cr-primary" onClick={() => {setCelebrate(false); onExit?.();}}>Start the game <FiArrowRight aria-hidden="true" /></button>
    {pack.preview && !onExit && <button className="cs-tutorial-undo" onClick={restart}>Replay introduction</button>}
  </main></div>;

  const lesson = pack.lessons[pack.completedSteps];
  const accepted = lesson.answers.includes(selected);
  const choose = (verdict, flag) => {
    if (!active || !ready || savingLock.current) return;
    const answer = flag || verdict;
    setSelected(answer); setError('');
    trackCommunity('cs_tutorial_answered', {tutorial_version: pack.version, step: pack.completedSteps + 1, answer, accepted: lesson.answers.includes(answer), test_mode: Boolean(pack.preview)});
  };
  const undo = () => { if (active && !savingLock.current) { setSelected(null); setError(''); } };
  const save = async () => {
    if (!active || !accepted || savingLock.current) return;
    savingLock.current = true; setSaving(true); setError('');
    try {
      const progress = await api.answer({version: pack.version, lessonId: lesson.id, answer: selected});
      if (!alive.current) return;
      if (progress.completed) {
        setCelebrate(true);
        trackCommunity('cs_tutorial_completed', {tutorial_version: pack.version, total_steps: pack.total, test_mode: Boolean(pack.preview)});
      }
      setPack(previous => ({...previous, ...progress}));
      setSelected(null); setShowHint(false); setReady(false);
    } catch (_) {
      if (alive.current) setError('Your answer is still here. Check your connection, then try saving again.');
    } finally {
      savingLock.current = false;
      if (alive.current) setSaving(false);
    }
  };
  return <div className="community-app cs-tutorial"><main className="cs-tutorial-round">
    <div className="cs-tutorial-progress-label"><span>{pack.preview ? 'PRACTICE' : 'FIELD PRACTICE'}</span>{pack.preview && <button type="button" disabled={saving} onClick={() => {setBypass(true); onExit?.();}}>Start spotting</button>}<span><strong>{pack.completedSteps + 1}</strong> / {pack.total}</span></div>
    <div className="cs-tutorial-progress" role="progressbar" aria-label="Practice progress" aria-valuemin={0} aria-valuemax={pack.total} aria-valuenow={pack.completedSteps}>
      {pack.lessons.map((item, index) => <span key={item.id} className={index < pack.completedSteps ? 'is-complete' : index === pack.completedSteps ? 'is-current' : ''} />)}
    </div>
    <header className="cs-tutorial-round-heading"><span className="cs-tutorial-level">{lesson.level}</span><h1 ref={heading} tabIndex={-1}>{lesson.title}</h1><p>{lesson.prompt}</p></header>
    <div className="cs-tutorial-round-body">
      <CitizenScienceTutorialClip key={`${lesson.id}:${reload}`} lesson={lesson} active={active} onReady={value => setReady(value !== false)} onRetry={retry} />
      <section className="cs-tutorial-response" aria-label="Your practice answer">
        {accepted ? <>
          <div className="cs-tutorial-feedback is-accepted" role="status"><strong><FiCheck aria-hidden="true" />{selected === 'unsure' ? 'Good judgement' : 'You’ve got it'}</strong><p>{lesson.feedbackByAnswer?.[selected] || lesson.feedback}</p></div>
          {error && <p className="cs-tutorial-save-error" role="alert">{error}</p>}
          <button className="cr-primary" disabled={!active || saving} onClick={save}>{saving ? 'Saving…' : error ? 'Try saving again' : pack.completedSteps + 1 === pack.total ? 'Finish practice' : 'Next clip'}<FiArrowRight aria-hidden="true" /></button>
          <button className="cs-tutorial-undo" disabled={!active || saving} onClick={undo}><FiRotateCcw aria-hidden="true" />Undo practice answer</button>
        </> : <>
          {selected && <div className="cs-tutorial-feedback" role="status"><strong>Here’s a clue</strong><p>{lesson.nudges?.[selected] || `${lesson.hint} Try ${lesson.answers.map(answer => labels[answer]).join(' or ')}.`}</p></div>}
          <div className="cs-tutorial-answer-prompt"><span>{ready ? 'Tap your answer' : 'Loading the clip…'}</span><button type="button" onClick={() => setShowHint(value => !value)} aria-expanded={showHint}><FiHelpCircle aria-hidden="true" />{showHint ? 'Hide clue' : 'Need a clue?'}</button></div>
          {showHint && <p className="cs-tutorial-hint">{lesson.hint}</p>}
          <MotionMobileActions disabled={!active || !ready || saving} onSubmit={choose} undoButton={<button type="button" className="cr-undo" disabled={!active || !selected || saving} onClick={undo}><FiRotateCcw aria-hidden="true" />Undo</button>} />
        </>}
        <p className="cs-tutorial-footnote">Practice only · your game score stays the same</p>
      </section>
    </div>
  </main></div>;
}
